import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

const DEFAULT_QR_MESSAGES = {
  en: "Fundraising for Campaign",
  fr: "Collecte de fonds pour la campagne",
};

const getPrintLanguage = (language) =>
  String(language || "en").toLowerCase().startsWith("fr") ? "fr" : "en";

const CampaignQrPrintDialog = ({
  open,
  t,
  uiLanguage,
  campaignNameEn,
  campaignNameFr,
  loadLogo,
  onCancel,
  onGenerate,
}) => {
  const initialLanguage = getPrintLanguage(uiLanguage);
  const campaignNames = {
    en: campaignNameEn || "",
    fr: campaignNameFr || "",
  };

  const [printLanguage, setPrintLanguage] = useState(initialLanguage);
  const [campaignName, setCampaignName] = useState(campaignNames[initialLanguage]);
  const [message, setMessage] = useState(DEFAULT_QR_MESSAGES[initialLanguage]);
  const [showCampaignName, setShowCampaignName] = useState(true);
  const [showMessage, setShowMessage] = useState(true);
  const [logoUrl, setLogoUrl] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    const language = getPrintLanguage(uiLanguage);
    setPrintLanguage(language);
    setCampaignName(language === "fr" ? campaignNameFr || "" : campaignNameEn || "");
    setMessage(DEFAULT_QR_MESSAGES[language]);
    setShowCampaignName(true);
    setShowMessage(true);
    setLogoUrl("");
    setLoading(false);

    let active = true;
    loadLogo().then((url) => {
      if (active) setLogoUrl(url);
    });

    return () => {
      active = false;
    };
  }, [open, uiLanguage, campaignNameEn, campaignNameFr, loadLogo]);

  const handleLanguageChange = (language) => {
    const previousDefault = DEFAULT_QR_MESSAGES[printLanguage];
    const previousCampaignName = campaignNames[printLanguage];
    const nextCampaignName = campaignNames[language];

    setPrintLanguage(language);
    setCampaignName((currentCampaignName) =>
      currentCampaignName === previousCampaignName || currentCampaignName.trim() === ""
        ? nextCampaignName
        : currentCampaignName
    );
    setMessage((currentMessage) =>
      currentMessage === previousDefault || currentMessage.trim() === ""
        ? DEFAULT_QR_MESSAGES[language]
        : currentMessage
    );
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      await onGenerate({
        language: printLanguage,
        message: showMessage ? message : "",
        campaignName: showCampaignName ? campaignName : "",
        logoUrl,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} fullWidth maxWidth="md">
      <DialogTitle>{t("campaign_qr_print_title")}</DialogTitle>

      <DialogContent className="!pt-2">
        <FormControl>
          <FormLabel>{t("campaign_qr_print_language")}</FormLabel>
          <RadioGroup
            row
            value={printLanguage}
            onChange={(event) => handleLanguageChange(event.target.value)}
          >
            <FormControlLabel
              value="en"
              control={<Radio />}
              label={t("english")}
              disabled={loading}
            />
            <FormControlLabel
              value="fr"
              control={<Radio />}
              label={t("french")}
              disabled={loading}
            />
          </RadioGroup>
        </FormControl>

        <Divider sx={{ my: 2.5 }} />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.1fr) minmax(280px, 0.9fr)" },
            gap: 3,
            alignItems: "start",
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
              {t("campaign_qr_print_content")}
            </Typography>

            <Box sx={{ mb: 2.5 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showMessage}
                    onChange={(event) => setShowMessage(event.target.checked)}
                    disabled={loading}
                  />
                }
                label={t("campaign_qr_print_show_message")}
                sx={{ mb: 0.5 }}
              />
              <TextField
                label={t("campaign_qr_print_message")}
                helperText={t("campaign_qr_print_message_helper")}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                multiline
                minRows={3}
                fullWidth
                disabled={loading || !showMessage}
                inputProps={{ maxLength: 500 }}
              />
            </Box>

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={showCampaignName}
                    onChange={(event) => setShowCampaignName(event.target.checked)}
                    disabled={loading}
                  />
                }
                label={t("campaign_qr_print_show_campaign_name")}
                sx={{ mb: 0.5 }}
              />
              <TextField
                label={t("campaign_qr_print_campaign_name")}
                helperText={t("campaign_qr_print_campaign_name_helper")}
                value={campaignName}
                onChange={(event) => setCampaignName(event.target.value)}
                fullWidth
                disabled={loading || !showCampaignName}
                inputProps={{ maxLength: 200 }}
              />
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
              {t("campaign_qr_print_preview")}
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                minHeight: 390,
                px: 3,
                py: 3.5,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                bgcolor: "background.default",
              }}
            >
              {logoUrl ? (
                <Box
                  component="img"
                  src={logoUrl}
                  alt=""
                  sx={{
                    maxWidth: 120,
                    maxHeight: 80,
                    objectFit: "contain",
                    mb: 2,
                  }}
                />
              ) : null}

              {showMessage && message.trim() ? (
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 600,
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                    mb: 1.5,
                  }}
                >
                  {message}
                </Typography>
              ) : null}

              {showCampaignName && campaignName.trim() ? (
                <Typography
                  sx={{
                    fontSize: 24,
                    fontWeight: 700,
                    overflowWrap: "anywhere",
                    mb: 2.5,
                  }}
                >
                  {campaignName}
                </Typography>
              ) : null}

              <Box
                sx={{
                  width: 150,
                  height: 150,
                  border: "2px dashed",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  {t("campaign_qr_print_qr_code")}
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onCancel} disabled={loading}>
          {t("cancel")}
        </Button>
        <Button variant="contained" onClick={handleGenerate} disabled={loading}>
          {loading ? t("campaign_qr_print_generating") : t("campaign_qr_print_generate")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CampaignQrPrintDialog;
